import { ReactNode, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './MobilePageHeader.module.scss';

type MobilePageHeaderProps = {
    /** Screen title. Truncates with an ellipsis so `actions` always stay on the row. */
    title: string;
    /** Right-hand control cluster. Order is the caller's — it differs per screen. */
    actions: ReactNode;
};

/**
 * The sticky title row for a mobile list screen, sitting below the app-wide MobileHeader.
 *
 * Owns the scroll-shadow: a zero-height sentinel renders above the bar, and once it scrolls out
 * of view the bar grows its bottom border. Consumers get that for free rather than repeating it.
 *
 * DealsList still carries its own copy because its sticky box is a column holding a second row
 * (the category tabs) that has to stay inside the border. Serving it needs either a `below` slot
 * or splitting the sticky shell out from this row — not another hand-rolled observer.
 */
const MobilePageHeader = ({ title, actions }: MobilePageHeaderProps) => {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [stuck, setStuck] = useState(false);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), { threshold: 0 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <div ref={sentinelRef} />
            <div className={clsx(styles.Bar, { [styles.Stuck]: stuck })}>
                <div className={styles.Title}>{title}</div>
                <div className={styles.Actions}>{actions}</div>
            </div>
        </>
    );
};

export default MobilePageHeader;
